import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KPICard } from "@/components/collections/kpi-card";
import {
  Search,
  DollarSign,
  Target,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  CheckCircle2,
  X,
  ArrowRightCircle,
  FileDown,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useReceiptsPageData } from "@/hooks/useReceiptsData";
import type { AllocationStatus } from "@/types/payments";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import * as XLSX from "xlsx";

const DEFAULT_RECEIPT_COLUMNS: ColumnDefinition[] = [
  { id: 'receiptNumber', label: 'Receipt #', visible: true, order: 0 },
  { id: 'customerName', label: 'Customer', visible: true, order: 1 },
  { id: 'currency', label: 'Currency', visible: true, order: 2 },
  { id: 'customerAccountNumber', label: 'Account Number', visible: false, order: 3 },
  { id: 'receiptDate', label: 'Receipt Date', visible: true, order: 4 },
  { id: 'receivedAmount', label: 'Received Amount', visible: true, order: 5 },
  { id: 'allocatedAmount', label: 'Allocated Amount', visible: false, order: 6 },
  { id: 'unappliedAmount', label: 'Unallocated Amount', visible: true, order: 7 },
  { id: 'allocationStatus', label: 'Status', visible: true, order: 8 },
  { id: 'paymentMethod', label: 'Payment Method', visible: false, order: 9 },
  { id: 'paymentReference', label: 'Payment Reference', visible: false, order: 10 },
  { id: 'bankReference', label: 'Bank Reference', visible: false, order: 11 },
  { id: 'importedAt', label: 'Imported At', visible: false, order: 12 },
  { id: 'importBatchId', label: 'Import Batch ID', visible: false, order: 13 },
  { id: 'notes', label: 'Notes', visible: false, order: 14 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 15 },
  { id: 'createdAt', label: 'Created At', visible: false, order: 16 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 17 },
  { id: 'modifiedAt', label: 'Modified At', visible: false, order: 18 },
  { id: 'actions', label: 'Actions', visible: true, order: 19 },
];

type SortField = "receiptNumber" | "customerName" | "receiptDate" | "receivedAmount" | "unappliedAmount" | "allocationStatus";
type SortDirection = "asc" | "desc" | null;

export default function ReceiptsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [promiseFilter, setPromiseFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const {
    columns: receiptColumns,
    savePreferences: saveReceiptColumnPreferences,
    resetToDefaults: resetReceiptColumnPreferences,
  } = useScreenColumnPreferences('receipts', DEFAULT_RECEIPT_COLUMNS);
  const columnVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};
    receiptColumns.forEach((col) => { vis[col.id] = col.visible; });
    return vis;
  }, [receiptColumns]);

  // Get data from hook
  const { receipts, isLoading } = useReceiptsPageData();

  // Get unique currencies from receipts
  const availableCurrencies = useMemo(() => {
    const currencies = new Set(receipts.map(r => r.currency));
    return Array.from(currencies).sort((a, b) => a.localeCompare(b));
  }, [receipts]);

  // Calculate KPIs from receipts
  const kpis = useMemo(() => {
    const totalReceipts = receipts.length;
    const totalReceived = receipts.reduce((sum, r) => sum + r.receivedAmount, 0);
    
    const fullyAllocated = receipts.filter(x => x.allocationStatus === 'fully_allocated');
    const partiallyAllocated = receipts.filter(x => x.allocationStatus === 'partially_allocated');
    const unallocated = receipts.filter(x => x.allocationStatus === 'unallocated');
    
    return {
      totalReceipts,
      totalReceived,
      fullyApplied: fullyAllocated.reduce((sum, r) => sum + r.receivedAmount, 0),
      fullyAppliedCount: fullyAllocated.length,
      partiallyApplied: partiallyAllocated.reduce((sum, r) => sum + r.receivedAmount, 0),
      partiallyAppliedCount: partiallyAllocated.length,
      unapplied: unallocated.reduce((sum, r) => sum + r.receivedAmount, 0),
      unappliedCount: unallocated.length,
    };
  }, [receipts]);

  // Filter receipts based on search, status, and KPI filters
  const filteredReceipts = useMemo(() => {
    let filtered = receipts;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (receipt) =>
          receipt.receiptNumber.toLowerCase().includes(query) ||
          receipt.customerName.toLowerCase().includes(query) ||
          receipt.customerAccountNumber.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(x => x.allocationStatus === statusFilter);
    }

    // Apply promise filter
    if (promiseFilter !== "all") {
      if (promiseFilter === "promise") {
        filtered = filtered.filter((receipt) => receipt.hasPromise);
      } else if (promiseFilter === "non-promise") {
        filtered = filtered.filter((receipt) => !receipt.hasPromise);
      }
    }

    // Apply currency filter
    if (currencyFilter !== "all") {
      filtered = filtered.filter(x => x.currency === currencyFilter);
    }

    return filtered;
  }, [receipts, searchQuery, statusFilter, promiseFilter, currencyFilter]);

  // Sort receipts
  const sortedReceipts = useMemo(() => {
    if (!sortField || !sortDirection) return filteredReceipts;

    return [...filteredReceipts].sort((a, b) => {
      let aValue: string | number | Date = a[sortField];
      let bValue: string | number | Date = b[sortField];

      // Handle date sorting
      if (sortField === "receiptDate") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle numeric sorting
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortDirection === "asc") {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }, [filteredReceipts, sortField, sortDirection]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveReceiptColumnPreferences(columns);
  };

  const columnExportConfig: Array<[string, string, (r: typeof sortedReceipts[number]) => string | number]> = [
    ["receiptNumber", "Receipt #", (r) => r.receiptNumber],
    ["customerName", "Customer", (r) => r.customerName],
    ["currency", "Currency", (r) => r.currency],
    ["customerAccountNumber", "Account Number", (r) => r.customerAccountNumber],
    ["receiptDate", "Receipt Date", (r) => format(new Date(r.receiptDate), "dd/MM/yyyy")],
    ["receivedAmount", "Received Amount", (r) => r.receivedAmount],
    ["allocatedAmount", "Allocated Amount", (r) => r.allocatedAmount],
    ["unappliedAmount", "Unallocated Amount", (r) => r.unappliedAmount],
    ["allocationStatus", "Status", (r) => r.allocationStatus.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())],
    ["paymentMethod", "Payment Method", (r) => r.paymentMethod.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())],
    ["paymentReference", "Payment Reference", (r) => r.paymentReference],
    ["bankReference", "Bank Reference", (r) => r.bankReference || ""],
    ["importedAt","Imported At", (r) => format(new Date(r.importedAt), "dd/MM/yyyy HH:mm")],
    ["importBatchId", "Import Batch ID", (r) => r.importBatchId || ""],
    ["notes", "Notes", (r) => r.notes || ""],
    ["createdBy", "Created By", (r) => r.createdBy],
    ["createdAt", "Created At", (r) => format(new Date(r.createdAt), "dd/MM/yyyy HH:mm")],
    ["modifiedBy", "Modified By", (r) => r.modifiedBy || ""],
    ["modifiedAt", "Modified At", (r) => r.modifiedAt ? format(new Date(r.modifiedAt), "dd/MM/yyyy HH:mm") : ""],
  ];

  const handleExportToExcel = () => {
    const dataToExport = sortedReceipts.map((receipt) => {
      const row: Record<string, string | number> = {};
      for (const [key, label, getValue] of columnExportConfig) {
        if (columnVisibility[key]) {
          row[label] = getValue(receipt);
        }
      }
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Receipts");
    const filename = `receipts_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const getStatusBadge = (status: AllocationStatus) => {
    switch (status) {
      case "fully_allocated":
        return (
          <Badge variant="outline" className="border-green-500 text-green-500">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Fully Allocated
          </Badge>
        );
      case "partially_allocated":
        return (
          <Badge variant="outline" className="border-yellow-500 text-yellow-500">
            <AlertCircle className="mr-1 h-3 w-3" />
            Partially Allocated
          </Badge>
        );
      case "unallocated":
        return (
          <Badge variant="outline" className="border-red-500 text-red-500">
            <X className="mr-1 h-3 w-3" />
            Unallocated
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={Object.values(columnVisibility).filter(Boolean).length} className="text-center py-8">
            Loading receipts...
          </TableCell>
        </TableRow>
      );
    }
    if (sortedReceipts.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={Object.values(columnVisibility).filter(Boolean).length} className="text-center py-8">
            No receipts found
          </TableCell>
        </TableRow>
      );
    }
    return sortedReceipts.map((receipt) => (
      <TableRow key={receipt.id} className="hover:bg-muted/50">
        {columnVisibility.receiptNumber && (
          <TableCell className="font-medium">{receipt.receiptNumber}</TableCell>
        )}
        {columnVisibility.customerName && (
          <TableCell>
            <div>
              <div className="font-medium flex items-center gap-2">
                {receipt.customerName}
                {receipt.hasPromise && (
                  <Badge variant="outline" className="text-xs border-blue-500 bg-blue-50 text-blue-700">P2P</Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground">{receipt.customerAccountNumber}</div>
            </div>
          </TableCell>
        )}
        {columnVisibility.currency && (
          <TableCell>{receipt.currency}</TableCell>
        )}
        {columnVisibility.customerAccountNumber && (
          <TableCell>{receipt.customerAccountNumber}</TableCell>
        )}
        {columnVisibility.receiptDate && (
          <TableCell>{format(new Date(receipt.receiptDate), "dd/MM/yyyy")}</TableCell>
        )}
        {columnVisibility.receivedAmount && (
          <TableCell className="text-right font-medium">{formatCurrency(receipt.receivedAmount)}</TableCell>
        )}
        {columnVisibility.allocatedAmount && (
          <TableCell className="text-right">{formatCurrency(receipt.allocatedAmount || 0)}</TableCell>
        )}
        {columnVisibility.unappliedAmount && (
          <TableCell className="text-right">
            <span className={cn("font-medium", receipt.unappliedAmount > 0 ? "text-red-500" : "text-muted-foreground")}>
              {formatCurrency(receipt.unappliedAmount)}
            </span>
          </TableCell>
        )}
        {columnVisibility.allocationStatus && (
          <TableCell>{getStatusBadge(receipt.allocationStatus)}</TableCell>
        )}
        {columnVisibility.paymentMethod && <TableCell>{receipt.paymentMethod || "-"}</TableCell>}
        {columnVisibility.paymentReference && <TableCell>{receipt.paymentReference || "-"}</TableCell>}
        {columnVisibility.bankReference && <TableCell>{receipt.bankReference || "-"}</TableCell>}
        {columnVisibility.importedAt && (
          <TableCell>{receipt.importedAt ? format(new Date(receipt.importedAt), "dd/MM/yyyy HH:mm") : "-"}</TableCell>
        )}
        {columnVisibility.importBatchId && <TableCell>{receipt.importBatchId || "-"}</TableCell>}
        {columnVisibility.notes && <TableCell>{receipt.notes || "-"}</TableCell>}
        {columnVisibility.createdBy && <TableCell>{receipt.createdBy || "-"}</TableCell>}
        {columnVisibility.createdAt && (
          <TableCell>{receipt.createdAt ? format(new Date(receipt.createdAt), "dd/MM/yyyy HH:mm") : "-"}</TableCell>
        )}
        {columnVisibility.modifiedBy && <TableCell>{receipt.modifiedBy || "-"}</TableCell>}
        {columnVisibility.modifiedAt && (
          <TableCell>{receipt.modifiedAt ? format(new Date(receipt.modifiedAt), "dd/MM/yyyy HH:mm") : "-"}</TableCell>
        )}
        {columnVisibility.actions && (
          <TableCell className="text-right">
            <Button
              variant="default"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/payments/allocate/${receipt.id}`);
              }}
              className="gap-1"
            >
              <ArrowRightCircle className="h-4 w-4" />
              Allocate
            </Button>
          </TableCell>
        )}
      </TableRow>
    ));
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-6 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold tracking-tight">Receipts</h2>
          </div>
          <p className="text-muted-foreground">
            View and manage customer receipts and payment allocations
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Receipts"
          value={formatCurrency(kpis.totalReceived)}
          subtitle={`${kpis.totalReceipts} receipts`}
          icon={DollarSign}
          trend={{ value: "+12.5%", isPositive: true }}
          colorScheme="blue"
        />
        <KPICard
          title="Fully Allocated"
          value={formatCurrency(kpis.fullyApplied)}
          subtitle={`${kpis.fullyAppliedCount} receipts`}
          icon={CheckCircle2}
          trend={{ value: "+8.2%", isPositive: true }}
          colorScheme="green"
        />
        <KPICard
          title="Partially Allocated"
          value={formatCurrency(kpis.partiallyApplied)}
          subtitle={`${kpis.partiallyAppliedCount} receipts`}
          icon={AlertCircle}
          trend={{ value: "-3.1%", isPositive: false }}
          colorScheme="amber"
        />
        <KPICard
          title="Unallocated"
          value={formatCurrency(kpis.unapplied)}
          subtitle={`${kpis.unappliedCount} receipts`}
          icon={Target}
          trend={{ value: "-5.4%", isPositive: false }}
          colorScheme="red"
        />
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by receipt number, customer..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="fully_allocated">Fully Allocated</SelectItem>
                    <SelectItem value="partially_allocated">Partially Allocated</SelectItem>
                    <SelectItem value="unallocated">Unallocated</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={promiseFilter} onValueChange={setPromiseFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Promise Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Receipts</SelectItem>
                    <SelectItem value="promise">Promise to Pay</SelectItem>
                    <SelectItem value="non-promise">Non-Promise</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Currencies</SelectItem>
                    {availableCurrencies.map((currency) => (
                      <SelectItem key={currency} value={currency}>
                        {currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Export and Column Config */}
            <div className="flex gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportToExcel}
                className="gap-2"
                title="Export to Excel"
              >
                <FileDown className="h-4 w-4" />
                Export
              </Button>
              <ColumnConfigDialog
                columns={receiptColumns}
                onApply={handleColumnConfigApply}
                onReset={resetReceiptColumnPreferences}
              />
            </div>
          </div>
          
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {columnVisibility.receiptNumber && (
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort("receiptNumber")}
                    >
                      <div className="flex items-center">
                        Receipt #
                        {renderSortIcon("receiptNumber")}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.customerName && (
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort("customerName")}
                    >
                      <div className="flex items-center">
                        Customer
                        {renderSortIcon("customerName")}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.currency && (
                    <TableHead className="select-none">
                      Currency
                    </TableHead>
                  )}
                  {columnVisibility.customerAccountNumber && (
                    <TableHead className="select-none">
                      Account Number
                    </TableHead>
                  )}
                  {columnVisibility.receiptDate && (
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort("receiptDate")}
                    >
                      <div className="flex items-center">
                        Receipt Date
                        {renderSortIcon("receiptDate")}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.receivedAmount && (
                    <TableHead
                      className="cursor-pointer select-none text-right"
                      onClick={() => handleSort("receivedAmount")}
                    >
                      <div className="flex items-center justify-end">
                        Received Amount
                        {renderSortIcon("receivedAmount")}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.allocatedAmount && (
                    <TableHead className="text-right">
                      Allocated Amount
                    </TableHead>
                  )}
                  {columnVisibility.unappliedAmount && (
                    <TableHead
                      className="cursor-pointer select-none text-right"
                      onClick={() => handleSort("unappliedAmount")}
                    >
                      <div className="flex items-center justify-end">
                        Unallocated Amount
                        {renderSortIcon("unappliedAmount")}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.allocationStatus && (
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort("allocationStatus")}
                    >
                      <div className="flex items-center">
                        Status
                        {renderSortIcon("allocationStatus")}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.paymentMethod && (
                    <TableHead>Payment Method</TableHead>
                  )}
                  {columnVisibility.paymentReference && (
                    <TableHead>Payment Reference</TableHead>
                  )}
                  {columnVisibility.bankReference && (
                    <TableHead>Bank Reference</TableHead>
                  )}
                  {columnVisibility.importedAt && (
                    <TableHead>Imported At</TableHead>
                  )}
                  {columnVisibility.importBatchId && (
                    <TableHead>Import Batch ID</TableHead>
                  )}
                  {columnVisibility.notes && (
                    <TableHead>Notes</TableHead>
                  )}
                  {columnVisibility.createdBy && (
                    <TableHead>Created By</TableHead>
                  )}
                  {columnVisibility.createdAt && (
                    <TableHead>Created At</TableHead>
                  )}
                  {columnVisibility.modifiedBy && (
                    <TableHead>Modified By</TableHead>
                  )}
                  {columnVisibility.modifiedAt && (
                    <TableHead>Modified At</TableHead>
                  )}
                  {columnVisibility.actions && (
                    <TableHead className="text-right">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderTableBody()}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
